import React, { useState } from "react";
import toast from "react-hot-toast";
import api from "../../services/api";

const AddOnsForm = ({
  initialData = null,
  isEdit = false,
  onCancel = null,
  onSuccess = null,
}) => {
  // If editing, initialData is the full configuration document
  const [brand, setBrand] = useState(initialData?.brand || "gravy_ganj");
  const [category, setCategory] = useState(initialData?.category || []);
  const [groups, setGroups] = useState(initialData?.groups || []);

  const categoryOptions = {
    kepsa_queue: [
      "Veg Biryani",
      "Non-veg biryani",
      "Veg kepsa",
      "Non-veg kepsa",
      "Veg starters",
      "Non-veg starters",
      "Beverages",
    ],
    gravy_ganj: [
      "Veg rice bowl",
      "Non-veg rice bowl",
      "Veg main course",
      "Non-veg main course",
      "Veg starters",
      "Non-veg starters",
      "Beverages",
    ],
    gutful_greens: [
      "Low-Kcal Protein Biryani",
      "Protein bowls",
      "Salad bowls",
      "Veggie super salad",
      "Flavoured protein",
      "Beverages",
    ],
  };

  // If not in edit mode (creating new), we might want to fetch existing config when brand/category changes
  // But for simplicity, we'll assume "Add New" means starting fresh or user should edit existing.
  // Actually, better UX: if user selects Brand+Category that already exists, warn them or load it?
  // For now, let's keep it simple: "Add New" creates a new entry.
  // Wait, backend does Upsert. So frontend should probably reflect that.
  // Let's add a "Load Existing" button or auto-fetch?
  // Let's stick to the plan: Step 1 Select Brand/Cat, Step 2 Manage Groups.

  const handleAddGroup = () => {
    setGroups([
      ...groups,
      {
        name: "",
        isMultiSelect: false,
        isAvailable: true,
        items: [],
      },
    ]);
  };

  const handleRemoveGroup = (groupIndex) => {
    setGroups(groups.filter((_, i) => i !== groupIndex));
  };

  const handleGroupChange = (groupIndex, field, value) => {
    const updatedGroups = [...groups];
    updatedGroups[groupIndex][field] = value;
    setGroups(updatedGroups);
  };

  const handleAddItem = (groupIndex) => {
    const updatedGroups = [...groups];
    updatedGroups[groupIndex].items.push({ name: "", price: "", isAvailable: true });
    setGroups(updatedGroups);
  };

  const handleRemoveItem = (groupIndex, itemIndex) => {
    const updatedGroups = [...groups];
    updatedGroups[groupIndex].items = updatedGroups[groupIndex].items.filter(
      (_, i) => i !== itemIndex
    );
    setGroups(updatedGroups);
  };

  const handleItemChange = (groupIndex, itemIndex, field, value) => {
    const updatedGroups = [...groups];
    updatedGroups[groupIndex].items[itemIndex][field] = value;
    setGroups(updatedGroups);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (category.length === 0) {
      toast.error("Please select at least one category");
      return;
    }

    if (groups.length === 0) {
      toast.error("Please add at least one add-on group");
      return;
    }

    // Validate groups
    for (const group of groups) {
      if (!group.name.trim()) {
        toast.error("All groups must have a name");
        return;
      }
      if (group.items.length === 0) {
        toast.error(`Group "${group.name}" must have at least one item`);
        return;
      }
      for (const item of group.items) {
        if (!item.name.trim() || item.price === "") {
          toast.error(`All items in "${group.name}" must have a name and price`);
          return;
        }
      }
    }

    const toastId = toast.loading("Saving add-on configuration...");

    try {
      const payload = {
        brand,
        category,
        groups,
      };

      if (isEdit && initialData?._id) {
        payload._id = initialData._id;
      }

      const response = await api.post("/addons", payload);

      if (response.data.success) {
        toast.success("Add-on configuration saved successfully!", {
          id: toastId,
        });
        if (onSuccess) onSuccess();
        if (!isEdit) {
          // Reset form if it was a new entry
          setBrand("gravy_ganj");
          setCategory([]);
          setGroups([]);
        }
      } else {
        toast.error(response.data.message || "Failed to save.", {
          id: toastId,
        });
      }
    } catch (error) {
      console.error("Save Add-on Error:", error);
      const errorMessage =
        error.response?.data?.message || "An error occurred.";
      toast.error(errorMessage, { id: toastId });
    }
  };

  const inputClass =
    "w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent";
  const buttonClass =
    "w-full py-3 rounded-lg text-white font-semibold disabled:opacity-50 transition";
  const subButtonClass =
    "px-3 py-1 rounded-lg text-xs font-semibold transition";

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h2 className="text-xl font-semibold text-gray-800 mb-6">
        {isEdit ? "Edit Add-on Configuration" : "Create/Update Add-on Configuration"}
      </h2>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Brand & Category Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Brand
            </label>
            <select
              value={brand}
              onChange={(e) => {
                setBrand(e.target.value);
                setCategory([]); // Reset category when brand changes
              }}
              className={inputClass}
              disabled={isEdit} // Disable changing brand/category in edit mode to avoid confusion
            >
              <option value="gravy_ganj">Gravy Ganj</option>
              <option value="kepsa_queue">Kepsa Queue</option>
              <option value="gutful_greens">Gutful Goodness - Greens</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-300 rounded-lg p-3 bg-white">
              {categoryOptions[brand]?.map((cat) => (
                <label key={cat} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    value={cat}
                    checked={category.includes(cat)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setCategory([...category, cat]);
                      } else {
                        setCategory(category.filter((c) => c !== cat));
                      }
                    }}
                    className="w-4 h-4 text-green-600 rounded focus:ring-green-500 border-gray-300"
                    disabled={isEdit} // Disable changing category in edit mode if desired, or allow it. Plan said allow it.
                  />
                  <span className="text-sm text-gray-700">{cat}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Groups Management */}
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-800">Add-on Groups</h3>
            <button
              type="button"
              onClick={handleAddGroup}
              className="px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition font-medium text-sm"
            >
              + Add Group
            </button>
          </div>

          {groups.length === 0 && (
            <div className="text-center py-8 text-gray-400 border-2 border-dashed border-gray-200 rounded-lg">
              No groups added yet. Click "+ Add Group" to start.
            </div>
          )}

          {groups.map((group, groupIndex) => (
            <div
              key={groupIndex}
              className="border border-gray-200 rounded-lg p-4 space-y-4 relative"
            >
              <button
                type="button"
                onClick={() => handleRemoveGroup(groupIndex)}
                className="absolute top-4 right-4 text-red-500 hover:text-red-700"
                title="Remove Group"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pr-8">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Group Name
                  </label>
                  <input
                    type="text"
                    value={group.name}
                    onChange={(e) =>
                      handleGroupChange(groupIndex, "name", e.target.value)
                    }
                    placeholder="e.g. Choice of Crust"
                    className={inputClass}
                  />
                </div>
                <div className="flex items-center h-full pt-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={group.isMultiSelect}
                      onChange={(e) =>
                        handleGroupChange(
                          groupIndex,
                          "isMultiSelect",
                          e.target.checked
                        )
                      }
                      className="w-4 h-4 text-green-600 rounded focus:ring-green-500 border-gray-300"
                    />
                    <span className="text-sm text-gray-700">
                      Allow Multiple Selection
                    </span>
                  </label>
                </div>
              </div>

              {/* Items in Group */}
              <div className="bg-gray-50 rounded-md p-3">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Items
                  </h4>
                  <button
                    type="button"
                    onClick={() => handleAddItem(groupIndex)}
                    className={`${subButtonClass} bg-blue-500 text-white hover:bg-blue-600`}
                  >
                    + Add Item
                  </button>
                </div>
                <div className="space-y-2">
                  {group.items.map((item, itemIndex) => (
                    <div
                      key={itemIndex}
                      className="grid grid-cols-[1fr_100px_40px_auto] gap-2 items-center"
                    >
                      <input
                        type="text"
                        value={item.name}
                        onChange={(e) =>
                          handleItemChange(
                            groupIndex,
                            itemIndex,
                            "name",
                            e.target.value
                          )
                        }
                        placeholder="Item Name"
                        className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                      />
                      <input
                        type="number"
                        value={item.price}
                        onChange={(e) =>
                          handleItemChange(
                            groupIndex,
                            itemIndex,
                            "price",
                            e.target.value
                          )
                        }
                        placeholder="Price"
                        className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                        min="0"
                      />
                      <div className="flex justify-center">
                        <input
                          type="checkbox"
                          checked={item.isAvailable ?? true}
                          onChange={(e) =>
                            handleItemChange(
                              groupIndex,
                              itemIndex,
                              "isAvailable",
                              e.target.checked
                            )
                          }
                          className="w-4 h-4 text-green-600 cursor-pointer rounded focus:ring-green-500 border-gray-300"
                          title="Is Item Available?"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(groupIndex, itemIndex)}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                  {group.items.length === 0 && (
                    <p className="text-xs text-gray-400 italic">
                      No items in this group.
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-4 pt-4 border-t border-gray-100">
          <button
            type="submit"
            className={`${buttonClass} bg-green-600 hover:bg-green-700`}
          >
            Save Configuration
          </button>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className={`${buttonClass} bg-gray-200 text-gray-800 hover:bg-gray-300`}
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default AddOnsForm;
